import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { DnaBackground } from "@/components/DnaBackground";
import { Navigation } from "@/components/Navigation";
import {
  getUserData,
  TeamStrengthsMapMember,
  TeamStrengthsMapTheme,
  ThemeSummary,
  themesAPI,
} from "@/lib/api";
import { translateDomainName, translateStrengthName } from "@/lib/strength-i18n";
import { STRENGTHS_EXPLORER_DOMAIN_COLORS } from "@/utils/constants/strengths";
import { chooseTeamStrengthsFit, TeamStrengthsFitResult } from "@/utils/teamStrengthsFit";
import { useToast } from "@/hooks/use-toast";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";

const DOMAIN_ORDER = ["Executing", "Influencing", "Relationship Building", "Strategic Thinking"];

const measureLongestNameRatio = (names: string[]) => {
  if (!names.length || typeof document === "undefined") return 7;
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) return 7;
  context.font = '600 100px system-ui, "Segoe UI", Tahoma, sans-serif';
  return Math.max(...names.map((name) => context.measureText(name).width)) / 100 || 7;
};

const splitName = (name: string) => {
  const [first = "", ...rest] = name.trim().split(/\s+/);
  return { first, family: rest.join(" ") };
};

const getDomainColor = (domain?: string, domainColor?: string | null) =>
  domainColor || (domain && STRENGTHS_EXPLORER_DOMAIN_COLORS[domain]) || "hsl(var(--primary))";

const getThemeDisplayName = (
  theme: { name: string; nameAr?: string | null },
  t: ReturnType<typeof useTranslation>["t"],
  language: string
) => {
  const englishName = theme.name;
  const arabicName = theme.nameAr || null;

  if (language?.split("-")[0] === "ar") {
    return {
      primary: arabicName || englishName,
      secondary: arabicName ? englishName : null,
    };
  }

  return {
    primary: englishName,
    secondary: arabicName ? arabicName : null,
  };
};

export default function TeamStrengthsMap() {
  const { themeId } = useParams();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user] = useState(() => getUserData());
  const [themes, setThemes] = useState<TeamStrengthsMapTheme[]>([]);
  const [members, setMembers] = useState<TeamStrengthsMapMember[]>([]);
  const [theme, setTheme] = useState<ThemeSummary | null>(null);
  const [maxCount, setMaxCount] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [fit, setFit] = useState<TeamStrengthsFitResult | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!user) {
      navigate("/auth");
    }
  }, [navigate, user]);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        if (themeId) {
          const response = await themesAPI.getTeamStrengthsMapTheme(themeId);
          setTheme(response.data.theme);
          setMembers(response.data.members || []);
        } else {
          const response = await themesAPI.getTeamStrengthsMap();
          setThemes(response.data.themes || []);
          setMaxCount(response.data.maxCount || 1);
          setMembers([]);
          setTheme(null);
        }
      } catch (error: any) {
        toast({
          title: t("teamStrengthsMap.loadFailed"),
          description: getLocalizedErrorMessage(error, "teamStrengthsMap.loadFailedDesc"),
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      load();
    }
  }, [themeId, t, toast, user]);

  const recalculateFit = useCallback(() => {
    if (!stageRef.current || !themeId || members.length === 0) return;
    const rect = stageRef.current.getBoundingClientRect();
    const longestNameRatio = measureLongestNameRatio(members.map((member) => member.name));
    setFit(chooseTeamStrengthsFit({
      width: rect.width,
      height: rect.height,
      count: members.length,
      longestNameRatio,
    }));
  }, [members, themeId]);

  useEffect(() => {
    recalculateFit();
    const timer = window.setTimeout(recalculateFit, 80);
    return () => window.clearTimeout(timer);
  }, [recalculateFit]);

  useEffect(() => {
    let resizeTimer: number | undefined;
    const onResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(recalculateFit, 80);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.clearTimeout(resizeTimer);
      window.removeEventListener("resize", onResize);
    };
  }, [recalculateFit]);

  useEffect(() => {
    document.body.style.overflow = themeId && fit && !fit.scroll ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [fit, themeId]);

  const detailColor = getDomainColor(theme?.domain, theme?.domainColor);

  const gridStyle = useMemo(() => {
    if (!fit) return undefined;
    return {
      "--team-cols": fit.cols,
      "--team-card-width": `${fit.cardWidth}px`,
      "--team-photo-height": `${Math.floor(fit.photoHeight)}px`,
      "--team-name-size": `${fit.fontSize.toFixed(1)}px`,
      "--team-gap": `${fit.gap}px`,
    } as CSSProperties;
  }, [fit]);

  if (themeId) {
    return (
      <div className="h-screen overflow-hidden bg-background" dir={isRtl ? "rtl" : "ltr"}>
        <DnaBackground />
        <Navigation user={user} />
        <main className="relative z-10 flex h-[calc(100vh-4rem)] flex-col gap-3 px-4 pb-4 pt-20 sm:px-6">
          {isLoading || !theme ? (
            <Card className="flex flex-1 items-center justify-center gap-3 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              {t("teamStrengthsMap.loading")}
            </Card>
          ) : (
            <>
              <header className="relative shrink-0 text-center">
                <Button
                  variant="outline"
                  className="absolute top-0 gap-2 rounded-full ltr:left-0 rtl:right-0"
                  onClick={() => {
                    navigate("/team-strengths-map");
                    window.setTimeout(() => window.scrollTo(0, 0), 0);
                  }}
                >
                  <ArrowLeft className={`h-4 w-4 ${isRtl ? "rotate-180" : ""}`} />
                  {t("teamStrengthsMap.allThemes")}
                </Button>
                <h1 className="px-28 text-3xl font-bold tracking-normal sm:text-4xl" style={{ color: detailColor }}>
                  {getThemeDisplayName(theme, t, i18n.language).primary}
                </h1>
                <div className="mt-1 text-sm text-muted-foreground" dir="ltr">{theme.name}</div>
              </header>

              <div ref={stageRef} className={`min-h-0 flex-1 ${fit?.scroll ? "overflow-y-auto" : "overflow-hidden"}`}>
                {members.length === 0 ? (
                  <Card className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center text-muted-foreground">
                    <Users className="h-8 w-8 opacity-50" />
                    {t("teamStrengthsMap.emptyMembers")}
                  </Card>
                ) : (
                  <section
                    className={`mx-auto grid justify-center ${fit?.scroll ? "content-start py-2" : "h-full content-center"}`}
                    style={{
                      ...gridStyle,
                      gap: "var(--team-gap)",
                      gridTemplateColumns: "repeat(var(--team-cols), var(--team-card-width))",
                    }}
                    aria-live="polite"
                  >
                    {members.map((member) => {
                      const { first, family } = splitName(member.name);
                      const firstColor = getDomainColor(member.topDomains?.[0]?.domain || theme.domain);
                      const familyColor = getDomainColor(member.topDomains?.[1]?.domain || member.topDomains?.[0]?.domain || theme.domain);
                      return (
                        <article
                          key={member.userId}
                          className="relative overflow-hidden rounded-xl bg-card shadow-sm"
                          style={{ width: "var(--team-card-width)" }}
                        >
                          <div
                            className="flex items-center justify-center bg-muted text-muted-foreground"
                            style={{
                              width: "var(--team-card-width)",
                              height: "var(--team-photo-height)",
                              fontSize: "calc(var(--team-card-width) * 0.38)",
                            }}
                          >
                            {member.profileImageDisplayUrl || member.profileImage ? (
                              <img
                                src={member.profileImageDisplayUrl || member.profileImage || undefined}
                                alt={member.name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              member.name.trim().charAt(0).toUpperCase()
                            )}
                          </div>
                          <span
                            className={`absolute right-1.5 top-1.5 flex items-center justify-center rounded-full bg-card px-1.5 font-semibold tabular-nums ${
                              member.rank <= 10 ? "text-foreground" : "text-muted-foreground"
                            }`}
                            style={{ minWidth: 28, height: 28, fontSize: 14 }}
                          >
                            {member.rank}
                          </span>
                          <div
                            className="px-2 py-2 text-center font-semibold leading-snug"
                            style={{ fontSize: "var(--team-name-size)", whiteSpace: "nowrap" }}
                          >
                            <span style={{ color: firstColor }}>{first}</span>{" "}
                            <span style={{ color: familyColor }}>{family}</span>
                          </div>
                        </article>
                      );
                    })}
                  </section>
                )}
              </div>
            </>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden" dir={isRtl ? "rtl" : "ltr"}>
      <DnaBackground />
      <Navigation user={user} />
      <main className="relative z-10 px-4 pb-12 pt-24 sm:px-6">
        <div className="container mx-auto max-w-6xl space-y-6">
          <Button variant="ghost" className="gap-2 px-0" asChild>
            <Link to="/dashboard" dir={isRtl ? "rtl" : "ltr"}>
              <ArrowLeft className={`h-4 w-4 ${isRtl ? "rotate-180" : ""}`} />
              {t("teamStrengthsMap.backToDashboard")}
            </Link>
          </Button>

          <header className="text-center">
            <h1 className="text-3xl font-bold tracking-normal sm:text-4xl">
              {t("teamStrengthsMap.title")}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("teamStrengthsMap.rankingRule")}
            </p>
          </header>

          <div className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground">
            {DOMAIN_ORDER.map((domain) => (
              <span key={domain} className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: getDomainColor(domain) }} />
                {translateDomainName(domain, t, i18n.language)}
              </span>
            ))}
          </div>

          {isLoading ? (
            <Card className="flex items-center justify-center gap-3 p-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              {t("teamStrengthsMap.loading")}
            </Card>
          ) : (
            <div className="grid gap-x-7 gap-y-1 md:grid-cols-2">
              {themes.map((item, index) => {
                const color = getDomainColor(item.domain, item.domainColor);
                const width = Math.round((item.count / Math.max(maxCount, 1)) * 100);
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => navigate(`/team-strengths-map/${item.id}`)}
                    className="grid w-full grid-cols-[2rem_minmax(0,1fr)_2.5rem] items-center gap-3 rounded-lg px-3 py-2 text-start transition-colors hover:bg-card focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    aria-label={t("teamStrengthsMap.themeAria", {
                      theme: getThemeDisplayName(item, t, i18n.language).primary,
                      count: item.count,
                    })}
                  >
                    <span className="text-center text-sm tabular-nums text-muted-foreground">{index + 1}</span>
                    <span className="min-w-0">
                      <span className="flex min-w-0 items-baseline gap-2">
                        <span className="whitespace-nowrap text-base font-semibold">
                          {getThemeDisplayName(item, t, i18n.language).primary}
                        </span>
                        <span className="truncate text-xs text-muted-foreground" dir="ltr">
                          {getThemeDisplayName(item, t, i18n.language).secondary || item.name}
                        </span>
                      </span>
                      <span className="mt-1.5 block h-1.5 overflow-hidden rounded-full bg-muted">
                        <span className="block h-full rounded-full" style={{ width: `${width}%`, backgroundColor: color }} />
                      </span>
                    </span>
                    <span className="text-center text-lg font-bold tabular-nums" style={{ color }}>
                      {item.count}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
